<template>
  <label
    class="btn btn-outline-light text-start w-100 pe-2"
    :class="{
      'bg-white': length(files) + length(available_files),
      'ps-2': length(files),
    }"
    :for="id"
    @drop.prevent.stop="create_file"
  >
    <div class="btn-toolbar">
      <div
        v-for="file in sorted_files"
        :key="file.path"
        class="input-group input-group-sm me-2"
      >
        <a
          class="btn p-0 px-1"
          :class="
            guess_filecheck(file.short_name)
              ? 'btn-success text-white'
              : file.invalid
                ? 'btn-danger  text-white'
                : 'btn-light'
          "
          :download="file.short_name"
          :href="file.url"
          target="_blank"
          :title="file.short_name"
          @click.prevent.stop="open_file(file)"
        >
          {{ truncate_chars(file.short_name) }}
        </a>
        <input
          v-if="file?.file_meta?.remote_name"
          :name="field"
          type="hidden"
          :value="file.file_meta.remote_name"
        />
        <a
          class="btn p-0 px-1"
          :class="
            guess_filecheck(file.short_name)
              ? 'btn-success text-white'
              : file.invalid
                ? 'btn-danger  text-white'
                : 'btn-light'
          "
          :disabled="upload_task"
          :title="translations.actions.DELETE"
          @click.prevent.stop="delete_file(file.path)"
        >
          &times;
        </a>
      </div>

      <div v-if="length(files) == 0">
        <icon class="me-1" name="FILE" />
        <span v-if="multiple">{{ translations.actions.DROP_FILES }}</span>
        <span v-else>{{ translations.actions.DROP_FILE }}</span>
        <span v-if="!is_staff" class="small fst-italic ms-1 text-muted">{{
          translations.actions.DROP_OR_CLICK
        }}</span>
      </div>
      <task_progress v-if="loading" />

      <div
        v-else-if="length(available_files) > 0"
        class="input-group input-group-sm ms-auto"
      >
        <div
          class="btn btn-sm btn-outline-light p-0 px-1"
          @click.prevent.stop="add_available_files"
        >
          {{ translations.actions.ADD }}
          {{ `${length(available_files)} file` }}
        </div>
        <div
          class="btn btn-sm btn-outline-light p-0 px-1"
          :title="translations.actions.DELETE"
          @click.prevent.stop="delete_available_files"
        >
          ×
        </div>
      </div>

      <div
        v-else-if="length(files) > 0"
        class="btn btn-outline-light p-0 px-1 border-0 ms-auto"
        :title="translations.actions.DELETE"
        @click.prevent.stop="delete_file()"
      >
        &times;
      </div>
    </div>

    <input
      v-show="false"
      :id="id"
      ref="file"
      :accept="accept"
      :multiple="multiple ? true : false"
      type="file"
      @change.prevent="create_file"
    />
  </label>
</template>

<script>
import {
  contains,
  filter,
  length,
  merge,
  object_map,
  scan,
  sort_by,
  to_array,
} from "@pdfking/rfunc"
import { Keys, Values } from "@pdfking/tfunc"
import { GENERIC_ERROR } from "@pdfking/translations/enum/errors"
import icon from "@pdfking/workflow/components/icon"
import fire_event from "@pdfking/workflow/utils/fire_event"
import make_translations from "@pdfking/workflow/utils/make_translations"
import proxy from "@pdfking/workflow/utils/proxy"
import truncate_chars from "@pdfking/workflow/utils/truncate_chars"
import uuid from "@pdfking/workflow/utils/uuid"
import delete_property from "@pdfking/workflow/vue/3/delete_property"

import closest from "../dom/closest"
import download_url from "../files/actions/download_url"
import upload_files from "../files/actions/upload_files"
import new_file_from_datatransfer from "../files/make/new_file_from_datatransfer"
import guess_extension from "../files/naming/guess_extension"
import guess_filecheck from "../files/naming/guess_filecheck"
import filenames from "../mixins/filenames"
import storage from "../state/storage"
import user from "../state/user"
import task_progress from "./task_progress"

export default {
  mixins: [filenames],
  props: ["field", "clear", "accept", "multiple"],
  emits: ["file-add", "file-change", "file-remove", "file-upload"],
  components: { task_progress, icon },
  data() {
    return {
      upload_task: null,
      files: {},
      loading: false,
      id: uuid(),
    }
  },
  mounted() {
    this.files = object_map(
      f => f.path,
      f => f,
      this.filenames
    )

    let submitted = 0

    this.$nextTick(() => {
      if (this.field) {
        const form = closest(this.$el, "form")
        if (form) {
          form.addEventListener("submit", event => {
            const local_files = filter(
              f => !f.file_meta.remote_id,
              Values(this.files)
            )

            if (length(local_files) > 0) {
              if (submitted >= 2) {
                throw GENERIC_ERROR
              }
              event.preventDefault()
              event.stopPropagation()
              this.upload_files(local_files).then(() =>
                fire_event(form, "submit")
              )
              submitted++
            }
          })
        }
      }
    })
  },
  computed: merge(
    proxy(storage, { selected_files: "global_files" }),
    proxy(user, ["is_staff"]),

    {
      translations: () => make_translations("actions"),

      available_files() {
        return filter(f => !contains(this.files, f.path), this.global_files)
      },
      sorted_files() {
        return sort_by(this.files, "short_name")
      },
    }
  ),
  methods: {
    length,
    guess_extension,
    guess_filecheck,
    truncate_chars,
    open_file(file) {
      file.get_url().then(u => download_url(u, file.short_name))
    },
    delete_file(id) {
      if (id) {
        const f = this.files[id]
        if (f) {
          delete_property(this.files, id)
          this.$emit("file-remove", f)

          if (this.is_staff) storage.add_files([f])
        }
      } else {
        scan(this.delete_file, Keys(this.files))
      }
    },
    delete_available_files() {
      storage.unselect_files(Keys(this.available_files))
    },
    push_file(file) {
      if (this.files[file.path]) {
        this.$emit("file-change", file)
      } else {
        this.$emit("file-add", file)
      }
      this.files[file.path] = file
    },
    async create_file(e) {
      // inject the form handler

      if (!this.multiple) this.files = {}

      const files = await Promise.all(
        to_array(e.dataTransfer ? e.dataTransfer.files : e.target.files).map(
          new_file_from_datatransfer
        )
      )

      scan(this.push_file, files)

      if (e.target) {
        e.target.value = null
      }
    },
    add_available_files() {
      scan(f => this.push_file(f), Values(this.available_files))
    },
    upload_files(files) {
      this.loading = true
      return upload_files(files).then(files => {
        this.$emit("file-upload", files)
        this.loading = false
        return files
      })
    },
  },
}
</script>

<style lang="scss" scoped></style>
